/**
 * Layout — ROS Shell with collapsible left sidebar navigation.
 *
 * Replaced the horizontal top nav with a vertical sidebar organized
 * into module sections (Growth, People, Intelligence, Sources, Admin).
 *
 * Features:
 *   - Collapsible sidebar (persisted in localStorage)
 *   - Collapsible section headers (persisted in localStorage)
 *   - Permission + feature flag gating (same canSee logic)
 *   - WHY/WHAT/HOW tooltips on nav items (300ms delay)
 *   - Agent Status Widget at sidebar bottom
 *   - Notification bell + account menu (initials avatar) in top bar
 *   - Light sidebar + hairline chrome in light mode; navy preserved in dark
 *   - Emulation controls preserved
 *   - Auto-collapse on mobile (<768px)
 *
 * (COORD-23: ROS Shell Wrap)
 */

import MobileBottomBar from "./MobileBottomBar";
import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { SIDEBAR_SECTIONS, NAV_TOOLTIPS, getSectionIcon, getItemIcon } from "../constants/sidebarNav";
import { normalizeLeaveJurisdiction } from "../utils/leaveJurisdiction";
import { showTeamPapsLink } from "../utils/papAccess";
import { preloadFrequentPages } from "../utils/preloadPages";
import PageLoading from "./PageLoading";
import NavTooltip from "./NavTooltip";
import AgentStatusWidget from "./AgentStatusWidget";
import HubSlideOutPanel from "./HubSlideOutPanel";
import FeedbackPanel from "./FeedbackPanel";
import IncidentReporter from "./IncidentReporter";
const WeeklyFocusModal = lazy(() => import("./WeeklyFocusModal"));
import CommandPalette from "./CommandPalette";
import WorldClock from "./WorldClock";
import VoiceNoteButton from "./VoiceNoteButton";
import { useTheme } from "../contexts/ThemeContext";
import { useLastVisitedTracker, usePageViewTracker } from "./LandingRedirect";
import { OnboardingProvider } from "./onboarding";
import { usePresentationChrome } from "../hooks/usePresentationChrome";
import TaNavTourBootstrap from "./onboarding/TaNavTourBootstrap";
import {
  House, Bell, Sun, Moon, CaretDown, Eye, Lightning, Warning, SidebarSimple, UserSwitch,
  X,
} from "@phosphor-icons/react";

const ROLE_BADGE_COLORS = {
  Admin: "bg-red-50 text-red-800 border border-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50",
  "BD Director": "bg-violet-50 text-violet-800 border border-violet-100 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900/50",
  "Senior Manager": "bg-violet-50 text-violet-800 border border-violet-100 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900/50",
  "Account Executive": "bg-emerald-50 text-emerald-800 border border-emerald-100 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900/50",
  "Capture Manager": "bg-sky-50 text-sky-800 border border-sky-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50",
  Analyst: "bg-sky-50 text-sky-800 border border-sky-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50",
  "Operations Support": "bg-amber-50 text-amber-800 border border-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50",
  "Staffing Lead": "bg-teal-50 text-teal-800 border border-teal-100 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-900/50",
  // Legacy fallbacks
  admin: "bg-red-50 text-red-800 border border-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50",
  manager: "bg-violet-50 text-violet-800 border border-violet-100 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900/50",
  analyst: "bg-sky-50 text-sky-800 border border-sky-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50",
  account_executive: "bg-emerald-50 text-emerald-800 border border-emerald-100 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900/50",
};

// Caret for section collapse/expand
const ChevronDown = ({ open }) => (
  <CaretDown
    size={12}
    weight="bold"
    className={`text-neutral-400 dark:text-text-muted transition-transform ${open ? "" : "-rotate-90"}`}
  />
);

// Renders a Phosphor icon component passed as `Icon` prop.
const NavIcon = ({ Icon, size = 18, weight = "regular", className = "" }) => {
  if (!Icon) return null;
  return <Icon size={size} weight={weight} className={`flex-shrink-0 ${className}`.trim()} />;
};

/** Structural nav markers (not clickable destinations). */
function isNavMarker(item) {
  return !!(item?.subHeader || item?.topicTab);
}

/**
 * Collapse flat sidebar items into render nodes. Consecutive `{ topicTab }` markers
 * group following links into topic tabs (one topic visible at a time).
 */
function buildNavRenderNodes(items, isItemActive, topicOverrides = {}) {
  const nodes = [];
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    if (item.subHeader) {
      nodes.push({ type: "subHeader", label: item.subHeader });
      i += 1;
      continue;
    }
    if (item.topicTab) {
      const tabs = [];
      const blockStart = i;
      while (i < items.length && items[i].topicTab) {
        const tabLabel = items[i].topicTab;
        const tabId = items[i].topicId || tabLabel.toLowerCase().replace(/\s+/g, "-");
        i += 1;
        const tabItems = [];
        while (i < items.length && !isNavMarker(items[i])) {
          tabItems.push(items[i]);
          i += 1;
        }
        if (tabItems.length > 0) tabs.push({ id: tabId, label: tabLabel, items: tabItems });
      }
      if (tabs.length > 0) {
        const blockKey = tabs.map((t) => t.id).join("|") || `block-${blockStart}`;
        const fromPath = tabs.find((t) => t.items.some((it) => it.to && isItemActive(it.to)));
        const selectedId = topicOverrides[blockKey] || fromPath?.id || tabs[0].id;
        nodes.push({ type: "topicTabs", blockKey, tabs, selectedId });
      }
      continue;
    }
    nodes.push({ type: "item", item });
    i += 1;
  }
  return nodes;
}

/** Two-letter initials for header avatar (first + last word, or first two chars). */
function displayNameInitials(displayName) {
  if (!displayName?.trim()) return "?";
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0].charAt(0);
    const b = parts[parts.length - 1].charAt(0);
    return (a + b).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

const BADGE_POLL_MS = 60000;

/** setInterval that skips ticks while the tab is hidden — background tabs
 *  generate no API load; the next tick after the tab is shown refreshes. */
function pollWhileVisible(fetchFn, intervalMs) {
  const iv = setInterval(() => {
    if (!document.hidden) fetchFn();
  }, intervalMs);
  return () => clearInterval(iv);
}

export default function Layout() {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const {
    user, logout, hasPermission, canAccessRd, hasRole, isAdmin,
    isEmulating, startEmulation, stopEmulation,
    isActingAs, startActingAs, stopActingAs,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Persist current path so opening ROS at "/" later lands the user
  // back where they were (LandingRedirect reads this on next visit).
  useLastVisitedTracker();
  usePageViewTracker();

  // ── Sidebar state ──────────────────────────────────────────────
  // Always start expanded on login; never restore a collapsed sidebar from storage.
  const [collapsed, setCollapsed] = useState(false);

  // Mobile-only: below md the sidebar becomes a fixed bottom bar (one icon
  // per section) instead of the icon-only rail desktop uses when collapsed.
  // Tapping a section opens a sheet with just that section's items; the bottom
  // bar itself switches sections. `null` = closed.
  const [mobileActiveSection, setMobileActiveSection] = useState(null);
  // Viewport X of the bottom-bar icon whose sheet is open, so the sheet's caret can point at it.
  const [mobileCaretX, setMobileCaretX] = useState(null);

  // Sections start all collapsed; user preferences persist within the session via localStorage.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const saved = localStorage.getItem("ros_sidebar_sections");
    if (saved) return JSON.parse(saved);
    return Object.fromEntries(SIDEBAR_SECTIONS.map((s) => [s.id, true]));
  });

  useEffect(() => {
    localStorage.setItem("ros_sidebar_sections", JSON.stringify(collapsedSections));
  }, [collapsedSections]);

  useEffect(() => {
    if (user) preloadFrequentPages(hasPermission);
  }, [user, hasPermission]);

  // Manual topic-tab selection when the current route is not under that block
  const [navTopicTabs, setNavTopicTabs] = useState({});

  // Route changes reset manual tab picks so the active page's topic stays selected,
  // and close the mobile section panel so picking a destination doesn't leave it open.
  useEffect(() => {
    setNavTopicTabs({});
    setMobileActiveSection(null);
  }, [pathname]);

  // Auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setCollapsed(true);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lead Triage's Presentation Mode (screen-share in the revenue meeting)
  // hides both the sidebar and the top chrome so the row list fills the
  // screen. TriageCenter fires this via a custom event rather than a prop,
  // since it's a routed child several levels below Layout — see
  // usePresentationChrome. The sidebar peeks open on hovering the left
  // edge (self-contained here); the header/banners just stay hidden —
  // TriageCenter's floating "Exit Presentation Mode" button is the way
  // back, not a hover peek.
  const { presentationMode: presentationModeStored } = usePresentationChrome();
  // Presentation Mode is toggled from a button that only exists on the
  // Lead Triage page — if it stayed on based purely on the stored/last-
  // broadcast value, navigating away (e.g. clicking a nav link found via
  // the sidebar's own hover-reveal) would leave the chrome hidden on
  // unrelated pages with no way to turn it back off. Gating on the route
  // means leaving Lead Triage always restores normal chrome, and coming
  // back re-enters presentation mode automatically if it was left on.
  const isTriageRoute = pathname === "/triage" || pathname.startsWith("/triage/") || pathname === "/leads" || pathname.startsWith("/leads/");
  const presentationMode = presentationModeStored && isTriageRoute;
  const [sidebarHovered, setSidebarHovered] = useState(false);
  useEffect(() => {
    if (!presentationMode) setSidebarHovered(false);
  }, [presentationMode]);
  const hideHeaderChrome = presentationMode;

  const toggleSection = (sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Section header click: if sidebar is collapsed, expand it and open only the clicked section.
  // If sidebar is already open, just toggle that section.
  const handleSectionClick = (section) => {
    if (collapsed) {
      setCollapsed(false);
      setCollapsedSections((prev) => {
        const next = { ...prev };
        SIDEBAR_SECTIONS.forEach((s) => { next[s.id] = s.id !== section.id; });
        return next;
      });
    } else {
      toggleSection(section.id);
    }
    // Prefer first nav item the user can actually open (granular T&A admin
    // perms / permissionAny), else fall back to the section defaultRoute.
    const firstAllowed = (section.items || []).find(
      (i) => i.to && !isNavMarker(i) && canSee(i)
    );
    const landing = firstAllowed
      ? typeof firstAllowed.resolveTo === "function"
        ? firstAllowed.resolveTo(hasPermission)
        : firstAllowed.to
      : section.defaultRoute;
    if (landing) navigate(landing);
  };

  // Dashboard/Home link: collapse all sections. If all already collapsed, expand all.
  const handleDashboardClick = (e) => {
    e.preventDefault();
    const visibleSections = SIDEBAR_SECTIONS.filter(sectionVisible);
    const allCollapsed = visibleSections.every((s) => collapsedSections[s.id]);
    if (allCollapsed) {
      // Expand all
      const next = { ...collapsedSections };
      visibleSections.forEach((s) => { next[s.id] = false; });
      setCollapsedSections(next);
    } else {
      // Collapse all
      const next = { ...collapsedSections };
      visibleSections.forEach((s) => { next[s.id] = true; });
      setCollapsedSections(next);
    }
    navigate("/home");
  };

  // Role display: prefer role_name from RBAC, fall back to legacy role
  const roleName = user?.role_name || user?.role || "";

  // ── Feature flags for nav gating (COORD-11) ─────────────────────
  const [enabledFlags, setEnabledFlags] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    const loadFlags = async () => {
      try {
        const res = await fetch("/api/platform-control/flags/enabled");
        if (res.ok) {
          const data = await res.json();
          setEnabledFlags(new Set(data.enabled_flags || []));
        }
      } catch {
        // On error, assume all enabled (fail-open)
        setEnabledFlags(new Set());
      }
    };
    loadFlags();
    // Refresh flags every 2 minutes
    return pollWhileVisible(loadFlags, 120000);
  }, [user]);

  // Helper: check if user can see a nav item (permissions + feature flags)
  const canSee = (item) => {
    // Sub-header / topic-tab markers are always visible (filtered later if empty)
    if (item.subHeader || item.topicTab) return true;
    if (item.hidden) return false;
    const rdRoleBypass = item.to?.startsWith("/growth/rd-") && canAccessRd(item.permission || "rd.view");
    if (rdRoleBypass) return true;
    // permissionAny: item.permission plus any narrower alternative permission
    // that should also unlock it (e.g. iso.view OR the narrower
    // iso.documents.view for a "Document Matrix only" role) — mirrors the
    // backend's require_any_permission for the same route.
    if (item.permissionAny && item.permissionAny.some((p) => hasPermission(p))) {
      // satisfied via the OR list — skip the single-permission check below
    } else if (item.permission && !hasPermission(item.permission)) {
      // Dual access: system Admins (admin.settings) may use T&A Admin tools
      const taAdminOr =
        item.permission === "timekeeping.admin" &&
        (hasPermission("admin.settings") || isAdmin());
      const taApproveOr =
        item.permission === "timekeeping.approve" &&
        (hasPermission("pto.approve") ||
          hasPermission("timekeeping.admin") ||
          hasPermission("admin.settings") ||
          isAdmin());
      // HR (pto.admin) may use the leave-side T&A Admin tools
      const ptoAdminOr =
        item.permission === "pto.admin" &&
        (hasPermission("timekeeping.admin") ||
          hasPermission("admin.settings") ||
          isAdmin());
      // Attendance Admin — also visible to legacy Leave/Time admin until roles are trimmed
      const attendanceAdminOr =
        item.permission === "timekeeping.attendance.admin" &&
        (hasPermission("pto.admin") ||
          hasPermission("timekeeping.admin") ||
          hasPermission("admin.settings") ||
          isAdmin());
      if (!taAdminOr && !taApproveOr && !ptoAdminOr && !attendanceAdminOr) return false;
    }
    // Feature flag check — if no flag specified, always show.
    // If flags haven't loaded yet (empty set), fail-open (show all).
    if (item.featureFlag && enabledFlags.size > 0 && !enabledFlags.has(item.featureFlag)) return false;
    // adminOnly items only visible to Admin role
    if (item.adminOnly && roleName !== "Admin") return false;
    // managerOnly items only visible to people with direct reports (or admins)
    if (item.managerOnly && !user?.is_manager && !hasPermission("rro.admin")) return false;
    // Jurisdiction-gated items (e.g. Daily Attendance is Colombia-only)
    if (item.jurisdiction) {
      const j = normalizeLeaveJurisdiction(
        user?.jurisdiction,
        user?.employment_type,
        user?.office_location
      );
      if (j !== item.jurisdiction) return false;
    }
    // My PAP / Team PAPs only make sense to show when there's actually one
    // open (a US leader always sees Team PAPs, to open an extraordinary one).
    // Each count starts null (not yet fetched) so it fails open.
    if (item.to === "/people/my-pap" && myPapCount === 0) return false;
    if (
      item.to === "/people/team-paps" &&
      !showTeamPapsLink({
        openPapCount: teamPapsCount,
        jurisdiction: normalizeLeaveJurisdiction(user?.jurisdiction, user?.employment_type, user?.office_location),
      })
    ) {
      return false;
    }
    return true;
  };

  // Helper: check if a section should be visible
  const sectionVisible = (section) => {
    const hasRdRoleBypassItem = section.items.some(
      (i) => i.to?.startsWith("/growth/rd-") && canAccessRd(i.permission || "rd.view")
    );
    // Section-level feature flag check
    if (section.featureFlag && enabledFlags.size > 0 && !enabledFlags.has(section.featureFlag) && !hasRdRoleBypassItem) return false;
    // At least one real item (not markers) must be visible
    return section.items.some((i) => !isNavMarker(i) && canSee(i));
  };

  const flagEnabled = useCallback(
    (flag) => {
      if (!flag) return true;
      // Fail-open while flags are still loading (empty set)
      if (enabledFlags.size === 0) return true;
      return enabledFlags.has(flag);
    },
    [enabledFlags]
  );

  const prepareTourStep = useCallback((step) => {
    if (!step) return;
    // Ensure sidebar is expanded when highlighting nav chrome
    if (step.selector?.includes("data-tour='nav-") || step.selector?.includes('data-tour="nav-') || step.selector === "[data-tour='sidebar']") {
      setCollapsed(false);
    }
    if (step.expandSection) {
      setCollapsed(false);
      setCollapsedSections((prev) => ({
        ...prev,
        [step.expandSection]: false,
      }));
    }
  }, []);

  // ── Team Notes pending badge ───────────────────────────────────
  const canViewTeamNotes = hasPermission("ai_hub.team_notes");
  const [teamNotesPendingCount, setTeamNotesPendingCount] = useState(0);

  const fetchTeamNotesPendingCount = useCallback(async () => {
    if (!canViewTeamNotes) return;
    try {
      const res = await fetch("/api/hub/weekly-focus/team-notes/pending-count");
      if (res.ok) {
        const data = await res.json();
        setTeamNotesPendingCount(data.count || 0);
      }
    } catch {}
  }, [canViewTeamNotes]);

  useEffect(() => {
    if (user && canViewTeamNotes) {
      fetchTeamNotesPendingCount();
      return pollWhileVisible(fetchTeamNotesPendingCount, BADGE_POLL_MS);
    }
  }, [user, canViewTeamNotes, fetchTeamNotesPendingCount]);

  // ── CPM Check-In pending badges — employee acknowledge + leader review ──
  const canViewMyCheckin = hasPermission("rro.view");
  const canManageTeamCheckins = hasPermission("rro.manage_team");
  const [myCheckinPendingCount, setMyCheckinPendingCount] = useState(0);
  const [teamCheckinsPendingCount, setTeamCheckinsPendingCount] = useState(0);

  const fetchMyCheckinPendingCount = useCallback(async () => {
    if (!canViewMyCheckin) return;
    try {
      const res = await fetch("/api/cpm/checkin-reviews/pending-count");
      if (res.ok) {
        const data = await res.json();
        setMyCheckinPendingCount(data.count || 0);
      }
    } catch {}
  }, [canViewMyCheckin]);

  const fetchTeamCheckinsPendingCount = useCallback(async () => {
    if (!canManageTeamCheckins) return;
    try {
      const res = await fetch("/api/cpm/checkin-reviews/team/pending-count");
      if (res.ok) {
        const data = await res.json();
        setTeamCheckinsPendingCount(data.count || 0);
      }
    } catch {}
  }, [canManageTeamCheckins]);

  useEffect(() => {
    if (user && canViewMyCheckin) {
      fetchMyCheckinPendingCount();
      return pollWhileVisible(fetchMyCheckinPendingCount, BADGE_POLL_MS);
    }
  }, [user, canViewMyCheckin, fetchMyCheckinPendingCount]);

  useEffect(() => {
    if (user && canManageTeamCheckins) {
      fetchTeamCheckinsPendingCount();
      return pollWhileVisible(fetchTeamCheckinsPendingCount, BADGE_POLL_MS);
    }
  }, [user, canManageTeamCheckins, fetchTeamCheckinsPendingCount]);

  // ── My PAP / Team PAPs conditional visibility — hidden unless open ──────
  const canViewMyPap = hasPermission("rro.view");
  const [myPapCount, setMyPapCount] = useState(null);

  const fetchMyPapCount = useCallback(async () => {
    if (!canViewMyPap) return;
    try {
      const res = await fetch("/api/cpm/paps/mine/count", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMyPapCount(Number(data.count) || 0);
      }
    } catch {}
  }, [canViewMyPap]);

  useEffect(() => {
    if (user && canViewMyPap) {
      fetchMyPapCount();
      return pollWhileVisible(fetchMyPapCount, 30000);
    }
  }, [user, canViewMyPap, fetchMyPapCount]);

  const canViewTeamPaps = hasPermission("rro.manage_team");
  const [teamPapsCount, setTeamPapsCount] = useState(null);

  const fetchTeamPapsCount = useCallback(async () => {
    if (!canViewTeamPaps) return;
    try {
      const res = await fetch("/api/cpm/paps/team/count", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTeamPapsCount(Number(data.count) || 0);
      }
    } catch {}
  }, [canViewTeamPaps]);

  useEffect(() => {
    if (user && canViewTeamPaps) {
      fetchTeamPapsCount();
      return pollWhileVisible(fetchTeamPapsCount, 30000);
    }
  }, [user, canViewTeamPaps, fetchTeamPapsCount]);

  const canViewApprovals =
    hasPermission("timekeeping.approve") ||
    hasPermission("pto.approve") ||
    hasPermission("timekeeping.admin") ||
    hasPermission("admin.settings");
  // HR / T&A admin — org-wide queue lives under People Admin → Leave
  const canViewOrgApprovals =
    hasPermission("pto.admin") ||
    hasPermission("timekeeping.admin") ||
    hasPermission("admin.settings") ||
    isAdmin();
  const [approvalsPendingCount, setApprovalsPendingCount] = useState(0);
  const [orgApprovalsPendingCount, setOrgApprovalsPendingCount] = useState(0);

  const fetchApprovalsPendingCount = useCallback(async () => {
    if (!canViewApprovals) return;
    try {
      const res = await fetch("/api/timekeeping/approvals/pending-count", {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setApprovalsPendingCount(Number(data.count) || 0);
      }
    } catch {}
  }, [canViewApprovals]);

  const fetchOrgApprovalsPendingCount = useCallback(async () => {
    if (!canViewOrgApprovals) return;
    try {
      const res = await fetch(
        "/api/timekeeping/approvals/pending-count?scope=org",
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setOrgApprovalsPendingCount(Number(data.count) || 0);
      }
    } catch {}
  }, [canViewOrgApprovals]);

  useEffect(() => {
    if (user && canViewApprovals) {
      fetchApprovalsPendingCount();
      return pollWhileVisible(fetchApprovalsPendingCount, 30000);
    }
  }, [user, canViewApprovals, fetchApprovalsPendingCount]);

  useEffect(() => {
    if (user && canViewOrgApprovals) {
      fetchOrgApprovalsPendingCount();
      return pollWhileVisible(fetchOrgApprovalsPendingCount, 30000);
    }
  }, [user, canViewOrgApprovals, fetchOrgApprovalsPendingCount]);

  // ── Time: rejected timesheet badge ──
  const canViewTime = hasPermission("timekeeping.module");
  const [rejectedTimesheetCount, setRejectedTimesheetCount] = useState(0);

  const fetchRejectedTimesheetCount = useCallback(async () => {
    if (!canViewTime) return;
    try {
      const res = await fetch("/api/timekeeping/my-timesheets/rejected-count", {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setRejectedTimesheetCount(Number(data.count) || 0);
      }
    } catch {}
  }, [canViewTime]);

  useEffect(() => {
    if (user && canViewTime) {
      fetchRejectedTimesheetCount();
      return pollWhileVisible(fetchRejectedTimesheetCount, 60000);
    }
  }, [user, canViewTime, fetchRejectedTimesheetCount]);

  const pendingCountByPath = {
    "/hub/weekly-focus/team": teamNotesPendingCount,
    "/people/checkin": myCheckinPendingCount,
    "/people/team-reviews": teamCheckinsPendingCount,
    "/people/approvals": approvalsPendingCount,
    // People Admin → Leave (includes org Approvals tab)
    "/people/timesheet/admin/balances": orgApprovalsPendingCount,
    "/people/time": rejectedTimesheetCount,
    "/people/timesheet": rejectedTimesheetCount,
  };

  const pendingCountBySection = {
    people: (approvalsPendingCount || 0) + (rejectedTimesheetCount || 0),
    people_admin: orgApprovalsPendingCount || 0,
  };

  // ── Notification bell ──────────────────────────────────────────
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const coordPromise = fetch("/api/coord/notifications", { credentials: "include" }).catch(() => null);
      const helpdeskPromise = hasPermission("helpdesk.view")
        ? fetch("/api/helpdesk/notifications?unreadOnly=true", { credentials: "include" }).catch(() => null)
        : Promise.resolve(null);
      const [coordRes, helpdeskRes] = await Promise.all([coordPromise, helpdeskPromise]);

      let coordItems = [];
      let coordUnread = 0;
      if (coordRes?.ok) {
        const data = await coordRes.json();
        coordUnread = data.unread_count || 0;
        coordItems = (data.notifications || []).map((n) => {
          const wfId = n.data?.workflow_item_id || n.data?.pre_built_content?.workflow_item_id;
          return {
            ...n,
            source: "coord",
            href: wfId
              ? `/growth/marketing-responses/${wfId}`
              : n.data?.opportunity_id
                ? `/crm/opportunity/${n.data.opportunity_id}`
                : undefined,
            // MKTRESP-FOLLOWUP: the "Ver detalle" affordance is scoped to
            // this workflow-item deep link specifically, not to every
            // clickable notification (helpdesk tickets, Watchdog runs, etc.
            // already carry their own href and shouldn't show it).
            showDetailAffordance: Boolean(wfId),
          };
        });
      }

      let helpdeskItems = [];
      let helpdeskUnread = 0;
      if (helpdeskRes && helpdeskRes.ok) {
        const data = await helpdeskRes.json();
        helpdeskUnread = data.unreadCount || 0;
        helpdeskItems = (data.results || []).map((item) => {
          const relatedId = item.relatedId || item.related_id;
          return {
            id: `helpdesk:${item.id}`,
            title: item.title,
            message: item.body,
            is_read: item.isRead ?? item.is_read ?? false,
            created_at: item.createdAt || item.created_at,
            href: item.href || (relatedId ? `/helpdesk/${relatedId}` : "/helpdesk"),
            source: "helpdesk",
            helpdeskId: item.id,
          };
        });
      }

      const merged = [...helpdeskItems, ...coordItems].sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta;
      });
      setNotifications(merged);
      setUnreadCount(coordUnread + helpdeskUnread);
    } catch {}
  }, [hasPermission]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      return pollWhileVisible(fetchNotifications, 30000);
    }
  }, [user, fetchNotifications]);

  const markAllRead = async () => {
    try {
      const jobs = [fetch("/api/coord/notifications/read-all", { method: "PUT", credentials: "include" })];
      if (hasPermission("helpdesk.view")) {
        jobs.push(fetch("/api/helpdesk/notifications/read-all", { method: "POST", credentials: "include" }));
      }
      await Promise.all(jobs.map((p) => p.catch(() => null)));
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {}
  };

  const openNotification = async (n) => {
    if (n.source === "helpdesk" && n.helpdeskId && !n.is_read) {
      try {
        await fetch(`/api/helpdesk/notifications/${n.helpdeskId}/read`, {
          method: "PATCH",
          credentials: "include",
        });
      } catch {}
      setNotifications((prev) => prev.map((item) => (
        item.id === n.id ? { ...item, is_read: true } : item
      )));
      setUnreadCount((count) => Math.max(0, count - 1));
    }
    setShowNotifications(false);
    if (n.href) navigate(n.href);
  };

  // ── Resource Governor banner (RESOURCE-GOV) ─────────────────────
  const [governorStatus, setGovernorStatus] = useState(null);
  const [governorTick, setGovernorTick] = useState(Date.now());

  const fetchGovernorStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/resource-governor/status");
      if (res.ok) {
        setGovernorStatus(await res.json());
      }
    } catch {
      // Fail quiet — banner just hides
    }
  }, []);

  useEffect(() => {
    if (!user || (!isAdmin() && !hasRole("manager"))) return;
    fetchGovernorStatus();
    return pollWhileVisible(fetchGovernorStatus, 30000);
  }, [user, isAdmin, fetchGovernorStatus]);

  // Tick once per second so active session countdown stays live
  useEffect(() => {
    if (!governorStatus?.active_sessions?.length) return;
    const iv = setInterval(() => setGovernorTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [governorStatus?.active_sessions?.length]);

  const governorBanner = (() => {
    if (!governorStatus) return null;
    if (governorStatus.environment === "production") return null;
    const active = governorStatus.active_sessions || [];

    // Active sessions — show earliest-expiring countdown
    if (active.length > 0) {
      const soonest = [...active].sort(
        (a, b) => new Date(a.expires_at) - new Date(b.expires_at),
      )[0];
      const remainingMs = Math.max(0, new Date(soonest.expires_at).getTime() - governorTick);
      const remainingSec = Math.floor(remainingMs / 1000);
      const hours = Math.floor(remainingSec / 3600);
      const minutes = Math.floor((remainingSec % 3600) / 60);
      const seconds = remainingSec % 60;
      const countdown = hours > 0
        ? `${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`
        : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      const labels = active.map((s) => s.category).join(", ");
      return {
        tone: "blue",
        message: (
          <>
            <strong>Resource Governor active</strong>
            <span className="mx-2">&bull;</span>
            <span>Categories: {labels}</span>
            <span className="mx-2">&bull;</span>
            <span className="font-mono">Next expiry in {countdown}</span>
          </>
        ),
      };
    }

    // Governor enabled but no active sessions — inference blocked
    if (governorStatus.governor_enabled) {
      return {
        tone: "red",
        message: (
          <>
            <strong>Inference blocked</strong>
            <span className="mx-2">&bull;</span>
            <span>Resource Governor is on but no categories are active.</span>
          </>
        ),
      };
    }

    // Governor disabled in non-prod — warn that nothing is throttled
    return {
      tone: "amber",
      message: (
        <>
          <strong>Resource Governor is OFF</strong>
          <span className="mx-2">&bull;</span>
          <span>{governorStatus.environment.toUpperCase()} inference is unrestricted.</span>
        </>
      ),
    };
  })();

  // ── AI Hub slide-out panel ──────────────────────────────────────
  const [hubPanelOpen, setHubPanelOpen] = useState(false);

  // ── Feedback slide-out panel ────────────────────────────────────
  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);

  // ── Weekly Focus modal ────────────────────────────────────────────
  const [weeklyFocusOpen, setWeeklyFocusOpen] = useState(false);

  // Listen for custom event from HubPage redirect (?mode=weekly_focus)
  useEffect(() => {
    const handleOpenWeeklyFocus = () => setWeeklyFocusOpen(true);
    window.addEventListener("openWeeklyFocus", handleOpenWeeklyFocus);
    return () => window.removeEventListener("openWeeklyFocus", handleOpenWeeklyFocus);
  }, []);

  // Keyboard shortcut: Ctrl+Shift+A / Cmd+Shift+A
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "A") {
        e.preventDefault();
        setHubPanelOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ── Timesheet quick-status [TS] button ──────────────────────────
  const [tsStatus, setTsStatus] = useState(null);
  const [tsOpen, setTsOpen] = useState(false);
  const [tsQuickEntry, setTsQuickEntry] = useState({ code_id: "", hours: "", desc: "" });
  const [tsSaving, setTsSaving] = useState(false);
  const [tsSaveMsg, setTsSaveMsg] = useState(null);
  const tsRef = useRef(null);

  // Fetch quick status every 60s — temporarily disabled (TS button hidden)
  // useEffect(() => {
  //   if (!user) return;
  //   const fetchTs = async () => {
  //     try {
  //       const res = await fetch("/api/timekeeping/my-quick-status");
  //       if (res.ok) setTsStatus(await res.json());
  //     } catch {}
  //   };
  //   fetchTs();
  //   const iv = setInterval(fetchTs, 60000);
  //   return () => clearInterval(iv);
  // }, [user]);

  // Close TS panel on outside click
  useEffect(() => {
    if (!tsOpen) return;
    const handleClick = (e) => {
      if (tsRef.current && !tsRef.current.contains(e.target)) setTsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [tsOpen]);

  // Quick time entry submission
  const handleTsQuickSave = async () => {
    if (!tsStatus?.timesheet_id || !tsQuickEntry.code_id || !tsQuickEntry.hours) return;
    setTsSaving(true);
    setTsSaveMsg(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await fetch(`/api/timekeeping/timesheets/${tsStatus.timesheet_id}/entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          charge_code_id: tsQuickEntry.code_id,
          work_date: today,
          hours: parseFloat(tsQuickEntry.hours),
          description: tsQuickEntry.desc || undefined,
        }),
      });
      if (res.ok) {
        setTsSaveMsg("saved");
        setTsQuickEntry({ code_id: "", hours: "", desc: "" });
        // Refresh status
        const r2 = await fetch("/api/timekeeping/my-quick-status");
        if (r2.ok) setTsStatus(await r2.json());
        setTimeout(() => setTsSaveMsg(null), 2000);
      } else {
        const err = await res.json();
        setTsSaveMsg(err.detail || "Error saving");
        setTimeout(() => setTsSaveMsg(null), 4000);
      }
    } catch {
      setTsSaveMsg("Network error");
      setTimeout(() => setTsSaveMsg(null), 3000);
    }
    setTsSaving(false);
  };

  // ── Account menu (avatar + Profile / Emulate / Sign out) ────────
  const [emulUsers, setEmulUsers] = useState([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [emulateListOpen, setEmulateListOpen] = useState(false);
  const [delegateUsers, setDelegateUsers] = useState([]);
  const [delegateListOpen, setDelegateListOpen] = useState(false);
  const userMenuRef = useRef(null);

  const openEmulatePickerInMenu = async () => {
    try {
      const res = await fetch("/api/users/emulatable");
      if (res.ok) setEmulUsers(await res.json());
    } catch {
      setEmulUsers([]);
    }
    setEmulateListOpen(true);
  };

  const openDelegatePickerInMenu = async () => {
    try {
      const res = await fetch("/api/users/delegatable");
      if (res.ok) setDelegateUsers(await res.json());
    } catch {
      setDelegateUsers([]);
    }
    setDelegateListOpen(true);
  };

  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocMouseDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
        setEmulateListOpen(false);
        setDelegateListOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setUserMenuOpen(false);
        setEmulateListOpen(false);
        setDelegateListOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);

  // ── Check if a nav item is the active route ─────────────────────
  const isItemActive = (path, matchPaths, excludePaths) => {
    const isUnder = (p) => pathname === p || pathname.startsWith(`${p}/`);
    if (Array.isArray(excludePaths) && excludePaths.some(isUnder)) return false;
    if (Array.isArray(matchPaths) && matchPaths.length > 0) {
      return matchPaths.some(isUnder);
    }
    // Personnel list lives at exact /people — must not match /people/time, /people/rro, etc.
    if (path === "/people") return pathname === "/people";
    if (path === "/triage") return pathname === "/triage" || pathname.startsWith("/triage/") || pathname === "/leads" || pathname.startsWith("/leads/");
    if (path === "/leads") return pathname === "/leads" || pathname.startsWith("/leads/");
    if (path === "/pipeline") return pathname === "/pipeline" || pathname.startsWith("/pipeline/") || pathname === "/opportunities" || pathname.startsWith("/crm/opportunity") || pathname.startsWith("/crm/pulse") || pathname.startsWith("/crm/pipeline");
    if (path === "/opportunities") return pathname === "/opportunities" || pathname.startsWith("/opportunities/") || pathname.startsWith("/crm/");
    return pathname === path || pathname.startsWith(path + "/");
  };

  // Renders the section list (Nav sections). Extracted so the mobile
  // bottom-sheet drawer can reuse the exact same destinations/permission
  // logic as the desktop rail — always expanded-style (navCollapsed=false)
  // there, regardless of the desktop sidebar's own collapse toggle.
  // `onlySectionId` — used only by the mobile per-category panel — restricts
  // rendering to a single section and forces it open, with no header/toggle
  // (redundant when only one section is shown standalone). Desktop never
  // passes this, so its render path (and output) is untouched.
  const renderNavSections = (navCollapsed, onlySectionId = null) => (
    <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
      {SIDEBAR_SECTIONS.filter(sectionVisible)
        .filter((s) => !onlySectionId || s.id === onlySectionId)
        .map((section) => {
        const sectionOpen = onlySectionId ? true : !collapsedSections[section.id];
        const filteredItems = section.items.filter(canSee);
        // Drop orphan sub-headers (a sub-header with no visible items under it,
        // e.g. "CPM Admin" for a non-admin whose items are all hidden).
        const visibleItems = filteredItems.filter((item, i) => {
          if (item.topicTab) return true;
          if (!item.subHeader) return true;
          for (let j = i + 1; j < filteredItems.length; j++) {
            if (filteredItems[j].subHeader) break;
            if (!isNavMarker(filteredItems[j])) return true;
          }
          return false;
        });
        const sectionActive = visibleItems.some((i) => i.to && isItemActive(i.to, i.matchPaths, i.excludePaths));
        const renderNodes = buildNavRenderNodes(visibleItems, (path) => isItemActive(path), navTopicTabs);

        const renderNavItem = (item) => {
          const ItemIcon = getItemIcon(item.to);
          const itemActive = isItemActive(item.to, item.matchPaths, item.excludePaths);

          if (item.to === "/hub/weekly-focus") {
            return (
              <NavTooltip key={item.to} tooltip={NAV_TOOLTIPS[item.to]}>
                <button
                  onClick={() => setWeeklyFocusOpen(true)}
                  className={`w-full flex items-center gap-2 rounded-md text-xs transition-all duration-150 ease-out ${onlySectionId ? "pl-2.5" : "pl-8"} pr-2 py-2 border-l-2 ${
                    weeklyFocusOpen
                      ? "bg-[#09244B]/10 text-[#09244B] font-semibold border-[#09244B]"
                      : "text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100/60 border-transparent"
                  }`}
                >
                  <NavIcon Icon={ItemIcon} size={18} weight={weeklyFocusOpen ? "bold" : "regular"} />
                  <span className={onlySectionId ? "leading-tight" : "truncate"}>{item.label}</span>
                </button>
              </NavTooltip>
            );
          }

          const navTo =
            typeof item.resolveTo === "function"
              ? item.resolveTo(hasPermission)
              : item.to;
          const itemPendingCount = pendingCountByPath[item.to] || 0;
          const pendingBadge = itemPendingCount > 0;

          return (
            <NavTooltip key={item.to} tooltip={NAV_TOOLTIPS[item.to]}>
              <NavLink
                to={navTo}
                data-tour={
                  {
                    "/people/my-day": "nav-ta-dashboard",
                    "/people/shifts": "nav-ta-attendance",
                    "/people/leave": "nav-ta-leave",
                    "/people/time": "nav-ta-time",
                    "/people/timesheet": "nav-ta-time",
                    "/people/approvals": "nav-ta-approvals",
                  }[item.to] || undefined
                }
                className={() =>
                  `flex items-center gap-2 rounded-md text-xs transition-all duration-150 ease-out ${onlySectionId ? "pl-2.5" : "pl-[30px]"} pr-2 py-2 border-l-2 ${
                    itemActive
                      ? onlySectionId
                        // Mobile per-category panel — stronger highlight than desktop's
                        // subtle tint so the active destination reads clearly in the
                        // compact single-section list.
                        ? "bg-[#09244B]/15 text-[#09244B] font-semibold border-[#09244B] shadow-sm"
                        : "bg-[#09244B]/10 text-[#09244B] font-semibold border-[#09244B]"
                      : "text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100/60 border-transparent"
                  }`
                }
              >
                <NavIcon Icon={ItemIcon} size={18} weight={itemActive ? "bold" : "regular"} />
                <span className={onlySectionId ? "leading-tight" : "truncate"}>{item.label}</span>
                {pendingBadge && (
                  <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] bg-red-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center px-1 leading-none">
                    {itemPendingCount > 99 ? "99+" : itemPendingCount}
                  </span>
                )}
              </NavLink>
            </NavTooltip>
          );
        };

        return (
          <div key={section.id} data-tour={`nav-${section.id}`}>
            {/* Section header — omitted in single-section (mobile panel) mode:
                the bottom bar icon already identifies the category. */}
            {(() => {
              const sectionPending = pendingCountBySection[section.id] || 0;
              const sectionBadge =
                sectionPending > 0 ? (
                  <span
                    className="flex-shrink-0 min-w-[16px] h-[16px] bg-red-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center px-1 leading-none"
                    aria-label={`${sectionPending} pending`}
                  >
                    {sectionPending > 99 ? "99+" : sectionPending}
                  </span>
                ) : null;

              return onlySectionId ? null : !navCollapsed ? (
              <div className="w-full flex items-center gap-2 px-2 py-2 mt-3 first:mt-1 group">
                <button
                  onClick={() => handleSectionClick(section)}
                  className="flex items-center gap-2 flex-1 text-left min-w-0"
                >
                  <NavIcon
                    Icon={getSectionIcon(section.id)}
                    size={14}
                    weight="regular"
                    className="text-neutral-400 group-hover:text-brand-navy dark:text-text-muted dark:group-hover:text-white transition-colors"
                  />
                  <span className="text-[11px] font-semibold tracking-wide text-neutral-400 group-hover:text-brand-navy dark:text-text-muted dark:group-hover:text-white transition-colors flex-1 text-left truncate">
                    {section.label}
                  </span>
                  {!sectionOpen ? sectionBadge : null}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSection(section.id); }}
                  className="p-0.5 hover:bg-neutral-100 dark:hover:bg-white/10 rounded transition-colors"
                  title={sectionOpen ? "Collapse section" : "Expand section"}
                >
                  <ChevronDown open={sectionOpen} />
                </button>
              </div>
            ) : (
              <NavTooltip tooltip={{ why: section.label }}>
                <button
                  onClick={() => handleSectionClick(section)}
                  className={`relative w-full flex items-center justify-center py-3 mt-2 first:mt-0 border-t border-neutral-200 transition-colors ${
                    sectionActive ? "bg-[#09244B]/8" : "hover:bg-neutral-50"
                  }`}
                >
                  <NavIcon
                    Icon={getSectionIcon(section.id)}
                    size={20}
                    weight={sectionActive ? "bold" : "regular"}
                    className={sectionActive ? "text-[#09244B]" : "text-neutral-400"}
                  />
                  {sectionPending > 0 && (
                    <span className="absolute top-1.5 right-1.5 min-w-[14px] h-[14px] bg-red-500 text-white rounded-full text-[8px] font-bold flex items-center justify-center px-0.5 leading-none">
                      {sectionPending > 99 ? "99+" : sectionPending}
                    </span>
                  )}
                </button>
              </NavTooltip>
            );
            })()}

            {/* Items (indented under section header) — animated expand/collapse via
                grid-template-rows so the reveal matches the chevron's own transition
                instead of popping in/out instantly. */}
            {!navCollapsed && (
            <div
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                sectionOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
              aria-hidden={!sectionOpen}
            >
            <div className={onlySectionId ? "grid grid-cols-2 gap-x-1" : "overflow-hidden"}>
              {renderNodes.map((node) => {
                if (node.type === "subHeader") {
                  return (
                    <div key={`sh-${node.label}`} className={`flex items-center gap-2 pr-2 pt-3 pb-1 ${onlySectionId ? "col-span-2 pl-2" : "pl-4"}`}>
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-text-muted">
                        {node.label}
                      </span>
                      <div className="flex-1 border-t border-neutral-200 dark:border-white/10" />
                    </div>
                  );
                }

                if (node.type === "topicTabs") {
                  const activeTab = node.tabs.find((t) => t.id === node.selectedId) || node.tabs[0];
                  return (
                    <div key={`tt-${node.blockKey}`} className={`mb-0.5 ${onlySectionId ? "col-span-2" : ""}`}>
                      <div
                        role="tablist"
                        aria-label="Admin topics"
                        className="flex flex-wrap gap-1 pl-4 pr-2 pt-1 pb-1.5"
                      >
                        {node.tabs.map((tab) => {
                          const selected = tab.id === activeTab.id;
                          return (
                            <button
                              key={tab.id}
                              type="button"
                              role="tab"
                              aria-selected={selected}
                              onClick={() =>
                                setNavTopicTabs((prev) => ({ ...prev, [node.blockKey]: tab.id }))
                              }
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide transition-colors ${
                                selected
                                  ? "bg-[#09244B]/10 text-[#09244B]"
                                  : "text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100/80"
                              }`}
                            >
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>
                      {activeTab.items.map((item) => renderNavItem(item))}
                    </div>
                  );
                }

                return renderNavItem(node.item);
              })}
            </div>
            </div>
            )}
          </div>
        );
      })}

    </nav>
  );

  useEffect(() => {
    if (!mobileActiveSection) { setMobileCaretX(null); return undefined; }
    const iconButton = document.querySelector(`[data-nav-key="${mobileActiveSection}"]`);
    const bar = iconButton?.closest("nav");
    const measure = () => {
      const rect = iconButton?.getBoundingClientRect();
      if (rect) setMobileCaretX(rect.left + rect.width / 2);
    };
    measure();
    // The bar scrolls the tapped icon into view smoothly; re-measure while it moves.
    bar?.addEventListener("scroll", measure, { passive: true });
    const settleTimer = setTimeout(measure, 400);
    return () => { bar?.removeEventListener("scroll", measure); clearTimeout(settleTimer); };
  }, [mobileActiveSection]);

  return (
    <OnboardingProvider
      hasPermission={hasPermission}
      flagEnabled={flagEnabled}
      onPrepareStep={prepareTourStep}
    >
    <TaNavTourBootstrap />
    <div className="flex h-screen bg-neutral-50/90 dark:bg-neutral-bg1 overflow-hidden">
      {/* Presentation Mode: thin hover strip along the left edge to peek the
          sidebar back open without leaving the fullscreen row list. */}
      {presentationMode && (
        <div
          className="fixed inset-y-0 left-0 w-2 z-30"
          onMouseEnter={() => setSidebarHovered(true)}
          title="Hover to show navigation"
        />
      )}
      {/* ── Sidebar — light rail (minimal) / navy in dark mode ─────── */}
      <aside
        data-tour="sidebar"
        onMouseLeave={() => presentationMode && setSidebarHovered(false)}
        className={
          presentationMode
            ? `fixed inset-y-0 left-0 z-40 flex flex-col overflow-hidden border-neutral-200/80 dark:border-white/10 bg-neutral-50 text-neutral-700 dark:bg-neutral-bg2 dark:text-white shadow-2xl transition-[width] duration-200 ${
                sidebarHovered ? "w-60 border-r" : "w-0 border-r-0"
              }`
            : `hidden md:flex ${
                collapsed ? "w-16" : "w-60"
              } flex-col flex-shrink-0 transition-all duration-300 ease-in-out sticky top-0 h-screen overflow-hidden border-r border-neutral-200/80 dark:border-white/10 bg-neutral-50 text-neutral-700 dark:bg-neutral-bg2 dark:text-white`
        }
      >
        {/* Logo + collapse toggle */}
        <div className="h-14 flex items-center px-3 border-b border-neutral-200 dark:border-white/10 flex-shrink-0">
          <button onClick={handleDashboardClick} className="flex items-center gap-2 flex-shrink-0 group min-w-0" title="Click to toggle all menus">
            <img
              src={`${import.meta.env.BASE_URL}brand/${theme === "dark" ? "rs-mark-white.png" : "rs-mark-dark.png"}`}
              alt="Resolute Solutions"
              className="w-8 h-8 object-contain shrink-0"
            />
            <span
              className={`text-[11px] font-medium tracking-tight text-neutral-500 group-hover:text-brand-navy dark:text-text-secondary dark:group-hover:text-white leading-tight truncate whitespace-nowrap overflow-hidden transition-all duration-200 ${
                collapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
              }`}
            >
              Resolute
            </span>
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto p-1 text-neutral-400 hover:text-neutral-900 transition-colors rounded-md hover:bg-neutral-100 dark:text-text-muted dark:hover:text-white dark:hover:bg-white/10"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <SidebarSimple size={16} weight="regular" className={`transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Home link — also toggles all sections collapse/expand */}
        <div className="px-2 pt-2" data-tour="nav-dashboard">
          <NavTooltip tooltip={collapsed ? { why: "Dashboard", what: "Click to toggle all menu sections open/closed" } : null}>
            <button
              onClick={handleDashboardClick}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                pathname === "/home"
                  ? "bg-neutral-100 text-brand-navy dark:!bg-white/10 dark:!text-white"
                  : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 dark:text-text-muted dark:hover:text-white dark:hover:bg-white/10"
              }`}
              title="Dashboard — click to toggle all menus"
            >
              <House size={18} weight={pathname === "/home" ? "bold" : "regular"} className="flex-shrink-0" />
              <span
                className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${
                  collapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
                }`}
              >
                Dashboard
              </span>
            </button>
          </NavTooltip>
        </div>

        {/* Nav sections */}
        {renderNavSections(collapsed)}

        {/* Help & About — pinned to sidebar bottom, never modified */}
        <div className="border-t border-neutral-200 flex-shrink-0 px-2 py-2">
          <NavLink
            to="/help"
            data-tour="nav-help"
            className={({ isActive: a }) =>
              `flex items-center gap-2 py-1.5 rounded-md text-[11px] transition-colors ${
                collapsed ? "px-2 justify-center" : "pl-4 pr-2"
              } ${
                a
                  ? "bg-[#09244B]/10 text-[#09244B] font-medium"
                  : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50"
              }`
            }
          >
            <NavIcon Icon={getItemIcon("/help")} size={18} />
            <span
              className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${
                collapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
              }`}
            >
              Help
            </span>
          </NavLink>
          {roleName === "Admin" && (
            <NavLink
              to="/about"
              className={({ isActive: a }) =>
                `flex items-center gap-2 py-1.5 rounded-md text-[11px] transition-colors ${
                  collapsed ? "px-2 justify-center" : "pl-4 pr-2"
                } ${
                  a
                    ? "bg-[#09244B]/10 text-[#09244B] font-medium"
                    : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50"
                }`
              }
            >
              <NavIcon Icon={getItemIcon("/about")} size={18} />
              <span
                className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${
                  collapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
                }`}
              >
                About
              </span>
            </NavLink>
          )}
        </div>

        {/* Agent Status Widget at sidebar bottom */}
        {enabledFlags.has("system.agent_pools") && (
          <div className="border-t border-neutral-200 dark:border-white/10 flex-shrink-0">
            <AgentStatusWidget inSidebar collapsed={collapsed} />
          </div>
        )}
      </aside>

      {/* ── Mobile bottom nav — replaces the desktop rail below md. One
            icon per section; tapping opens a small panel with just that
            section's items (never the whole nav at once). Individual
            sections still expand/collapse exactly like desktop — same
            renderNavSections, same permission/flag gating. ── */}
      {(() => {
        const mobileSections = SIDEBAR_SECTIONS.filter(sectionVisible);
        const activeSection = mobileSections.find((s) => s.id === mobileActiveSection);
        return (
          <>
            {/* Compact per-category panel — sits just above the bottom bar */}
            {activeSection && (
              <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={activeSection.label}>
                <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={() => setMobileActiveSection(null)} />
                <div
                  key={activeSection.id}
                  className="absolute inset-x-3 bottom-[100px] max-h-[56vh] flex flex-col rounded-3xl bg-white dark:bg-neutral-bg2 shadow-2xl animate-bubble-pop"
                  style={{
                    transformOrigin: mobileCaretX != null
                      ? `${Math.min(Math.max(((mobileCaretX - 12) / (window.innerWidth - 24)) * 100, 0), 100)}% 100%`
                      : "50% 100%",
                  }}
                >
                  {mobileCaretX != null && (
                    <span
                      aria-hidden
                      className="absolute -bottom-2.5 w-5 h-5 rounded-br-[8px] bg-white dark:bg-neutral-bg2 rotate-45 shadow-[2px_2px_3px_-1px_rgba(0,0,0,0.12)]"
                      style={{ left: Math.min(Math.max(mobileCaretX - 8, 28), window.innerWidth - 16 - 28), transform: "translateX(-50%) rotate(45deg)" }}
                    />
                  )}
                  <div className="flex items-center justify-between px-4 pt-2.5 pb-1.5 border-b border-neutral-200 dark:border-white/10 flex-shrink-0">
                    <span className="flex items-center gap-2 text-sm font-semibold text-neutral-800 dark:text-white">
                      <NavIcon Icon={getSectionIcon(activeSection.id)} size={18} weight="bold" className="text-brand-blue dark:text-blue-300" />
                      {activeSection.label}
                    </span>
                    <button
                      onClick={() => setMobileActiveSection(null)}
                      className="p-2 -mr-1 rounded-full text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 dark:text-text-muted dark:hover:text-white dark:hover:bg-white/10"
                      aria-label="Close"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div key={activeSection.id} className="flex-1 overflow-y-auto px-2 py-2 pb-[env(safe-area-inset-bottom)] animate-fade-in">
                    {renderNavSections(false, activeSection.id)}
                  </div>
                </div>
              </div>
            )}

            {/* Fixed bottom bar — Home plus every visible section, equal-width
                icon-only slots so it never scrolls whatever the role sees. */}
            {(() => {
              const sectionItem = (section) => ({
                key: section.id,
                label: section.label,
                active: section.id === mobileActiveSection ||
                  (!mobileActiveSection && section.items.some((i) => i.to && isItemActive(i.to, i.matchPaths, i.excludePaths))),
                badge: pendingCountBySection[section.id] || 0,
                icon: (size) => <NavIcon Icon={getSectionIcon(section.id)} size={size} weight="bold" />,
                onClick: () => setMobileActiveSection(section.id === mobileActiveSection ? null : section.id),
              });
              const homeItem = {
                key: "home",
                label: "Home",
                active: !mobileActiveSection && pathname === "/home",
                icon: (size) => <House size={size} weight="bold" />,
                onClick: () => { setMobileActiveSection(null); handleDashboardClick({ preventDefault() {} }); },
              };
              return <MobileBottomBar items={[homeItem, ...mobileSections.map(sectionItem)]} ariaLabel="Sections" raised={Boolean(mobileActiveSection)} />;
            })()}
          </>
        );
      })()}

      {/* ── Main content area ────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Presentation Mode hides the header/banners (and LeadTriageLayout's
            tab strip + TriageCenter's own filter bar, stacked right below)
            entirely — TriageCenter's floating "Exit Presentation Mode"
            button is the way back, not a hover peek. */}
        <div
          className={`flex-shrink-0 transition-[max-height,opacity] duration-200 ${
            hideHeaderChrome
              ? "max-h-0 opacity-0 pointer-events-none overflow-hidden"
              : "max-h-[320px] opacity-100 overflow-visible"
          }`}
        >
        {/* Top bar: world clock + weather (preserved) + account tools */}
        <header className="relative z-20 h-14 flex items-center px-4 justify-between flex-shrink-0 bg-white/95 dark:bg-neutral-bg2 backdrop-blur-sm border-b border-neutral-200/90 dark:border-white/10">
          <span className="absolute left-0 top-0 bottom-0 w-px bg-brand-blue/90 rounded-r-full" aria-hidden />
          <div className="flex-1 min-w-0 pl-2">
            <WorldClock />
          </div>

          {user && (
            <div className="flex items-center gap-3 flex-shrink-0" data-tour="header-actions">
              {/* Voice Note recorder — temporarily hidden */}
              {/* <VoiceNoteButton variant="header" /> */}

              {/* Feedback button — temporarily hidden */}
              {/* {enabledFlags.has("module.feedback") && hasPermission("feedback.submit") && (
                <button
                  onClick={() => setFeedbackPanelOpen(true)}
                  className="p-1.5 rounded-lg text-neutral-500 hover:text-brand-navy dark:text-text-muted dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 dark:focus:ring-offset-neutral-bg2"
                  title="Submit feedback (bug, feature, automation)"
                  aria-label="Submit feedback"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </button>
              )} */}

              {/* Report issue — files an Internal app / ROS ticket with session logs */}
              <IncidentReporter />

              {/* Notification bell */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setUserMenuOpen(false);
                    setEmulateListOpen(false);
                  }}
                  className="relative p-1.5 rounded-lg text-neutral-500 hover:text-brand-navy dark:text-text-muted dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 dark:focus:ring-offset-neutral-bg2"
                  title="Notifications"
                >
                  <Bell size={20} weight={unreadCount > 0 ? "fill" : "regular"} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                {/* Notification dropdown — anchored to the bell, which sits
                    mid-header (theme toggle + avatar follow it), not the
                    viewport's right edge. Below sm, right-0 off that narrow
                    wrapper pushed a 320px-wide panel past the left edge —
                    clipped, not scrollable. Below sm it's viewport-anchored
                    (fixed + left/right margins) instead; sm+ is byte-
                    identical to the original absolute/right-0/w-80. */}
                {showNotifications && (
                  <div className="fixed left-4 right-4 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-1 sm:w-80 bg-white dark:bg-neutral-bg3 rounded-xl border border-neutral-200/90 dark:border-white/10 shadow-sm z-50 max-h-96 overflow-y-auto">
                    <div className="px-3 py-2 border-b flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">Notifications</span>
                      {unreadCount > 0 && (
                        <button type="button" onClick={markAllRead} className="text-[10px] font-medium text-brand-blue hover:underline focus:outline-none focus:ring-2 focus:ring-brand-blue rounded">Mark all read</button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-3 py-6 text-center text-xs text-gray-400">No notifications</div>
                    ) : notifications.slice(0, 15).map((n) => {
                      const clickable = Boolean(n.href);
                      const showDetail = clickable && n.showDetailAffordance;
                      const rowClass = n.is_read
                        ? "bg-white dark:bg-neutral-bg3"
                        : "bg-blue-50 dark:bg-blue-950/40";
                      return (
                        <div
                          key={n.id}
                          role={clickable ? "button" : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          onClick={clickable ? () => openNotification(n) : undefined}
                          onKeyDown={clickable ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openNotification(n);
                            }
                          } : undefined}
                          className={`px-3 py-2 border-b text-xs ${rowClass} hover:bg-gray-50 dark:hover:bg-white/5 ${clickable ? "cursor-pointer" : ""}`}
                        >
                          <div className="flex items-center gap-1">
                            <div className="font-medium text-gray-800 dark:text-gray-100">{n.title}</div>
                            {showDetail && (
                              <span className="text-[10px] font-medium text-brand-blue">↗</span>
                            )}
                          </div>
                          <div className="text-gray-500 dark:text-text-muted mt-0.5">{n.message}</div>
                          {showDetail && (
                            <div className="text-[10px] font-medium text-brand-blue mt-1 hover:underline">
                              Ver detalle →
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-lg text-neutral-500 hover:text-brand-navy dark:text-text-muted dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 dark:focus:ring-offset-neutral-bg2"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? <Sun size={16} weight="regular" /> : <Moon size={16} weight="regular" />}
              </button>

              {/* Notepad quick-access — temporarily hidden */}
              {/* <button
                onClick={() => navigate("/notepad")}
                className="relative p-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-all"
                title="Notepad — To Do + Journal"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
              </button> */}

              {/* Timesheet quick-entry [TS] button — temporarily hidden */}
              {/* <div className="relative" ref={tsRef}>
                <button
                  onClick={() => setTsOpen(!tsOpen)}
                  className={`relative px-2 py-1 text-xs font-bold rounded border transition-all ${
                    tsStatus?.is_late
                      ? "border-red-400 bg-red-50 text-red-700 dark:border-red-500 dark:bg-red-900/30 dark:text-red-400 animate-pulse"
                      : tsStatus?.status === "submitted" || tsStatus?.status === "approved"
                      ? "border-green-300 bg-green-50 text-green-700 dark:border-green-600 dark:bg-green-900/30 dark:text-green-400"
                      : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  }`}
                  title={tsStatus?.is_late ? "Timesheet overdue!" : "Quick Time Entry"}
                >
                  TS
                  {tsStatus?.is_late && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 animate-ping" />
                  )}
                </button>

                {tsOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl border border-neutral-200/90 dark:border-gray-700 shadow-sm z-50">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                          {tsStatus?.period?.label || "No Period"}
                        </span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                          tsStatus?.is_late ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" :
                          tsStatus?.status === "approved" ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400" :
                          tsStatus?.status === "submitted" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400" :
                          "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        }`}>
                          {tsStatus?.is_late ? "OVERDUE" : (tsStatus?.status || "—").toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="px-3 py-2 grid grid-cols-3 gap-2 border-b border-gray-100 dark:border-gray-700">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-800 dark:text-gray-100">{tsStatus?.today_hours ?? 0}</div>
                        <div className="text-[10px] text-gray-400">Today</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-lg font-bold ${
                          tsStatus?.period_hours < tsStatus?.expected_hours
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-gray-800 dark:text-gray-100"
                        }`}>{tsStatus?.period_hours ?? 0}</div>
                        <div className="text-[10px] text-gray-400">Period</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-400">{tsStatus?.expected_hours ?? 0}</div>
                        <div className="text-[10px] text-gray-400">Expected</div>
                      </div>
                    </div>
                    {tsStatus?.status === "draft" || tsStatus?.status === "rejected" ? (
                      <div className="px-3 py-2 space-y-2">
                        <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Entry — Today</div>
                        <select
                          value={tsQuickEntry.code_id}
                          onChange={(e) => setTsQuickEntry(prev => ({ ...prev, code_id: e.target.value }))}
                          className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                        >
                          <option value="">Select charge code...</option>
                          {(tsStatus?.charge_codes || []).map(cc => (
                            <option key={cc.id} value={cc.id}>
                              {cc.code} — {cc.display_name}
                            </option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min="0"
                            max="24"
                            step="0.25"
                            value={tsQuickEntry.hours}
                            onChange={(e) => setTsQuickEntry(prev => ({ ...prev, hours: e.target.value }))}
                            placeholder="Hours"
                            className="w-20 text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                          />
                          <input
                            type="text"
                            value={tsQuickEntry.desc}
                            onChange={(e) => setTsQuickEntry(prev => ({ ...prev, desc: e.target.value }))}
                            placeholder="Description (optional)"
                            className="flex-1 text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <button
                            onClick={handleTsQuickSave}
                            disabled={tsSaving || !tsQuickEntry.code_id || !tsQuickEntry.hours}
                            className="px-3 py-1 text-xs font-medium text-white bg-brand-blue hover:bg-brand-blue-hover disabled:bg-neutral-300 dark:disabled:bg-slate-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 dark:focus:ring-offset-slate-800"
                          >
                            {tsSaving ? "Saving..." : "Log Time"}
                          </button>
                          {tsSaveMsg && (
                            <span className={`text-[10px] font-medium ${
                              tsSaveMsg === "saved" ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"
                            }`}>
                              {tsSaveMsg === "saved" ? "Saved!" : tsSaveMsg}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="px-3 py-3 text-center text-xs text-gray-400 dark:text-gray-500">
                        Timesheet is {tsStatus?.status} — edit in full view
                      </div>
                    )}
                    <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={() => { navigate("/people/timesheet"); setTsOpen(false); }}
                        className="w-full text-center text-xs font-semibold text-brand-blue hover:text-brand-blue-hover dark:text-blue-300 dark:hover:text-white transition-colors focus:outline-none focus:underline"
                      >
                        Open Full Timesheet →
                      </button>
                    </div>
                  </div>
                )}
              </div> */}

              {/* Account: initials avatar + menu */}
              <div className="relative pl-1" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen((o) => {
                      const next = !o;
                      if (o) {
                        setEmulateListOpen(false);
                        setDelegateListOpen(false);
                      }
                      return next;
                    });
                  }}
                  className="flex items-center gap-1 rounded-full pr-1.5 sm:pr-2 py-0.5 border border-transparent hover:border-neutral-200 dark:hover:border-white/15 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-bg2"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                  aria-label={`Account menu for ${user.display_name || "user"}`}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-blue text-xs font-bold text-white shadow-sm ring-2 ring-white dark:ring-neutral-bg2 shrink-0">
                    {displayNameInitials(user.display_name)}
                  </span>
                  <CaretDown
                    size={14}
                    weight="bold"
                    className={`text-neutral-500 dark:text-text-muted shrink-0 transition-transform ${userMenuOpen ? "rotate-180" : ""}`}
                    aria-hidden
                  />
                </button>

                {userMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl bg-white dark:bg-neutral-bg3 shadow-sm border border-neutral-200/90 dark:border-white/10 z-[60] overflow-hidden py-1"
                  >
                    {!emulateListOpen && !delegateListOpen ? (
                      <>
                        <div className="px-4 py-3 border-b border-neutral-100 dark:border-white/10">
                          <p className="text-sm font-semibold text-brand-navy dark:text-white truncate" title={user.display_name}>
                            {user.display_name}
                          </p>
                          <span
                            className={`inline-block mt-1.5 px-2 py-0.5 text-[10px] font-semibold rounded-md ${
                              ROLE_BADGE_COLORS[roleName] ||
                              "bg-neutral-50 text-neutral-700 border border-neutral-200 dark:bg-neutral-bg4 dark:text-text-secondary dark:border-white/10"
                            }`}
                          >
                            {roleName}
                          </span>
                        </div>
                        <Link
                          role="menuitem"
                          to="/profile"
                          onClick={() => {
                            setUserMenuOpen(false);
                            setEmulateListOpen(false);
                          }}
                          className="flex w-full items-center px-4 py-2.5 text-sm font-medium text-brand-navy dark:text-text-secondary hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
                        >
                          Profile
                        </Link>
                        {hasPermission("admin.emulate") && !isEmulating && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={openEmulatePickerInMenu}
                            className="flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
                          >
                            Emulate user…
                          </button>
                        )}
                        {hasPermission("admin.act_as_delegate") && !isActingAs && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={openDelegatePickerInMenu}
                            className="flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
                          >
                            Act as…
                          </button>
                        )}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setUserMenuOpen(false);
                            setEmulateListOpen(false);
                            setDelegateListOpen(false);
                            logout();
                          }}
                          className="flex w-full items-center px-4 py-2.5 text-left text-sm font-medium text-neutral-600 dark:text-text-muted hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors border-t border-neutral-100 dark:border-white/10"
                        >
                          Sign out
                        </button>
                      </>
                    ) : emulateListOpen ? (
                      <>
                        <div className="px-3 py-2 border-b border-neutral-100 dark:border-white/10 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEmulateListOpen(false)}
                            className="text-xs font-semibold text-brand-blue dark:text-blue-300 hover:underline focus:outline-none focus:underline"
                          >
                            ← Back
                          </button>
                          <span className="text-xs font-semibold text-neutral-600 dark:text-text-muted truncate">
                            Emulate (read-only)
                          </span>
                        </div>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {emulUsers.length === 0 ? (
                            <div className="px-4 py-6 text-center text-xs text-neutral-500 dark:text-text-muted">
                              No users available
                            </div>
                          ) : (
                            emulUsers.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  startEmulation(u.id);
                                  setUserMenuOpen(false);
                                  setEmulateListOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-white/5 border-b border-neutral-50 dark:border-white/10 last:border-0 transition-colors"
                              >
                                <div className="font-medium text-brand-navy dark:text-text-primary truncate">{u.display_name}</div>
                                <div className="text-[11px] text-neutral-500 dark:text-text-muted truncate mt-0.5">
                                  {u.role_name} · {u.email}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    ) : delegateListOpen ? (
                      <>
                        <div className="px-3 py-2 border-b border-neutral-100 dark:border-white/10 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setDelegateListOpen(false)}
                            className="text-xs font-semibold text-brand-blue dark:text-blue-300 hover:underline focus:outline-none focus:underline"
                          >
                            ← Back
                          </button>
                          <span className="text-xs font-semibold text-neutral-600 dark:text-text-muted truncate">
                            Act as…
                          </span>
                        </div>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {delegateUsers.length === 0 ? (
                            <div className="px-4 py-6 text-center text-xs text-neutral-500 dark:text-text-muted">
                              No delegatable users
                            </div>
                          ) : (
                            delegateUsers.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  startActingAs(u.id);
                                  setUserMenuOpen(false);
                                  setDelegateListOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-white/5 border-b border-neutral-50 dark:border-white/10 last:border-0 transition-colors"
                              >
                                <div className="font-medium text-brand-navy dark:text-text-primary truncate">{u.display_name}</div>
                                <div className="text-[11px] text-neutral-500 dark:text-text-muted truncate mt-0.5">
                                  {u.email}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}
        </header>

        {/* Resource Governor banner */}
        {governorBanner && (
          <div
            className={`border-b px-4 py-2 ${
              governorBanner.tone === "red"
                ? "bg-red-50 border-red-200 text-red-800"
                : governorBanner.tone === "amber"
                  ? "bg-amber-50 border-amber-200 text-amber-800"
                  : "bg-blue-50 border-blue-200 text-blue-800"
            }`}
          >
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Lightning
                size={16}
                weight="fill"
                className={`flex-shrink-0 ${
                  governorBanner.tone === "red"
                    ? "text-red-600"
                    : governorBanner.tone === "amber"
                      ? "text-amber-600"
                      : "text-blue-600"
                }`}
              />
              <span className="text-sm">{governorBanner.message}</span>
              <button
                onClick={() => navigate("/settings/platform?tab=governor")}
                className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                  governorBanner.tone === "red"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : governorBanner.tone === "amber"
                      ? "bg-amber-600 text-white hover:bg-amber-700"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Open Resource Governor
              </button>
            </div>
          </div>
        )}

        {/* Maintenance mode banner */}
        {enabledFlags.has("system.maintenance_mode") && (
          <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2">
            <div className="flex items-center justify-center gap-2">
              <Warning size={16} weight="fill" className="text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">
                Maintenance Mode Active — write operations are restricted
              </span>
            </div>
          </div>
        )}

        {/* Emulation banner */}
        {isEmulating && (
          <div className="bg-amber-100 border-b border-amber-300 px-4 py-2">
            <div className="flex items-center justify-center gap-3">
              <Eye size={16} weight="regular" className="text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                Viewing as: <strong>{user?.display_name}</strong> ({user?.role_name})
                {user?.real_user_name && (
                  <span className="text-amber-600 font-normal"> &mdash; logged in as {user.real_user_name}</span>
                )}
              </span>
              <span className="text-xs text-amber-600 font-medium px-2 py-0.5 bg-amber-200 rounded">
                READ-ONLY
              </span>
              <button
                onClick={stopEmulation}
                className="px-3 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
              >
                Stop Emulating
              </button>
            </div>
          </div>
        )}

        {/* Act-as-delegate banner */}
        {isActingAs && (
          <div className="bg-blue-100 border-b border-blue-300 px-4 py-2">
            <div className="flex items-center justify-center gap-3">
              <UserSwitch size={16} weight="regular" className="text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                Acting on behalf of: <strong>{user?.display_name}</strong>
                {user?.acting_user_name && (
                  <span className="text-blue-600 font-normal"> &mdash; {user.acting_user_name}</span>
                )}
              </span>
              <button
                onClick={stopActingAs}
                className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Stop Acting As
              </button>
            </div>
          </div>
        )}
        </div>

        {/* Page content — context transition: fade + slight rise on module/route change,
            e.g. jumping sidebar sections (Growth -> Intelligence) */}
        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
          <div key={location.pathname} className="animate-slide-up">
            <Suspense fallback={<PageLoading />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {/* AI Hub slide-out panel (Ctrl+Shift+A shortcut still works) */}
      <HubSlideOutPanel open={hubPanelOpen} onClose={() => setHubPanelOpen(false)} />

      {/* Feedback slide-out panel */}
      <FeedbackPanel open={feedbackPanelOpen} onClose={() => setFeedbackPanelOpen(false)} />

      {/* Weekly Focus fullscreen modal — lazy loaded to keep it out of the main bundle */}
      {weeklyFocusOpen && (
        <Suspense fallback={null}>
          <WeeklyFocusModal open={weeklyFocusOpen} onClose={() => setWeeklyFocusOpen(false)} />
        </Suspense>
      )}

      {/* Command Palette (Ctrl+K) */}
      <CommandPalette />
    </div>
    </OnboardingProvider>
  );
}
